import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import NewsletterForm from "./NewsletterForm";

/* ───────── helper: breakpoint ───────── */
function useDesktop(min = 768) {
  const query = `(min-width:${min}px)`;
  const [desktop, setDesktop] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const mq = matchMedia(query);
    const fn = (e) => setDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [query]);
  return desktop;
}
/* ────────────────────────────────────── */

const GAP_BOTTOM = 64; // px (bottom-16)
const GAP_RIGHT = 24; // px (right-6)

/** Desktop-only floating newsletter card (sticks near presskit) */
export default function FloatingCtaCard({ className = "" }) {
  const heroRef = useRef(null);
  const contactRef = useRef(null);
  const isDesktop = useDesktop();

  useEffect(() => {
    heroRef.current = document.getElementById("cta-sentinel-hero");
    contactRef.current = document.getElementById("cta-sentinel-contact");
  }, []);

  const cardRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  const [pinTop, setPinTop] = useState(0);

  const heroIn = useInView(heroRef);
  const contactIn = useInView(contactRef);
  const visible = isDesktop && !heroIn && !contactIn;

  const onScroll = () => {
    if (!isDesktop) return;
    const presskit = document.getElementById("presskit");
    if (!presskit || !cardRef.current) return;

    const presskitTop = presskit.getBoundingClientRect().top + window.scrollY;
    const cardH = cardRef.current.offsetHeight;
    const sectionH = presskit.offsetHeight;

    const freezeTop = presskitTop + (sectionH - cardH) / 2;
    const topOfCard =
      window.scrollY + window.innerHeight - GAP_BOTTOM - cardH;
    const shouldStick = topOfCard >= freezeTop;

    if (shouldStick && pinTop !== freezeTop) setPinTop(freezeTop);
    setStuck((prev) => (prev === shouldStick ? prev : shouldStick));
  };

  useEffect(() => {
    if (!isDesktop) return undefined;
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop || !cardRef.current) return undefined;
    const ro = new ResizeObserver(onScroll);
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, [isDesktop]);

  if (!isDesktop) return null;

  const cardStyle = stuck
    ? { position: "absolute", top: pinTop, right: GAP_RIGHT }
    : { position: "fixed", bottom: GAP_BOTTOM, right: GAP_RIGHT };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={cardRef}
          key="floating-cta"
          initial={{ x: 320, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 320, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          style={cardStyle}
          className={`
            ${className}
            z-[40] space-y-4 md:w-[270px]
            rounded-xl bg-black/80 backdrop-blur-md p-5
            shadow-lg shadow-black/30
          `}
        >
          <h4 className="text-lg font-semibold mb-1">JOIN OUR NEWSLETTER</h4>
          <NewsletterForm />
          {/* TODO: Steam wishlist URL — re-enable when page exists
          <hr className="border-t border-white/15 my-3" />
          <AnimatedButton
            text="Wishlist on Steam"
            href="https://steamcommunity.com/id/twinframestudios/"
            className="w-full justify-center"
          />
          */}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
